import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ROUTE_DEFINITIONS } from '@/routes';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useUI } from '@/hooks/ui';
import { useUnsavedChangesWarning } from '@/hooks/ui/navigation';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { employeeContractService } from '@/api/domains/employeeContract/employeeContract.service.unified';
import { employeeGet, type EmployeeProfile } from '@/api/domains/employee';
import {
  mapEmployeeContractToForm,
  mapUpdateEmployeeContractFormToTransport,
} from '@/api/domains/employeeContract/mappers/employeeContractEdit.mapper';
import type {
  LC_GetEmployeeContractByIdOut,
  ValidatedUpdateEmployeeContractFormData,
} from '@/api/domains/employeeContract/types/employeeContractEdit.types';
import {
  updateEmployeeContractSchema,
  type UpdateEmployeeContractFormData,
} from '@/schemas/employee/employeeContractEdit.schema';
import {
  useHoldingCompanies,
  useContractStatuses,
  useContractTypes,
  useContractModalities,
  useAreas,
  usePositions,
  useCostCenters,
  useUnions,
} from '@/api/domains/catalog';


interface UseEmployeeContractEditResult {
  isLoading: boolean;
  updating: boolean;
  error: string | null;
  employeeProfile: EmployeeProfile | null;
  handleCancel: () => void;
  handleUpdate: () => Promise<void>;
  handleSaveContract: (contractData: ValidatedUpdateEmployeeContractFormData) => Promise<void>;
  contractStatusOptions: { value: string; label: string }[];
  contractTypeOptions: { value: string; label: string }[];
  contractModalityOptions: { value: string; label: string }[];
  companyOptions: { value: string; label: string }[];
  areaOptions: { value: string; label: string }[];
  positionOptions: { value: string; label: string }[];
  costCenterOptions: { value: string; label: string }[];
  unionOptions: { value: string; label: string }[];
  form: {
    register: ReturnType<typeof useForm<UpdateEmployeeContractFormData>>['register'];
    setValue: ReturnType<typeof useForm<UpdateEmployeeContractFormData>>['setValue'];
    watch: ReturnType<typeof useForm<UpdateEmployeeContractFormData>>['watch'];
    errors: ReturnType<typeof useForm<UpdateEmployeeContractFormData>>['formState']['errors'];
    trigger: ReturnType<typeof useForm<UpdateEmployeeContractFormData>>['trigger'];
    setError: ReturnType<typeof useForm<UpdateEmployeeContractFormData>>['setError'];
  };
}


// Pure helper function to build validated contract data - fully testable
// After Zod validation with refine(), we know these fields have valid positive numbers
export function buildValidatedContractData(
  formData: UpdateEmployeeContractFormData,
  employeeId: number,
): ValidatedUpdateEmployeeContractFormData {
  return {
    ...formData,
    employeeId,
    contractNumber: formData.contractNumber,
    contractStatusId: formData.contractStatusId,
    // After refine validation, these are guaranteed to be positive numbers
    contractTypeId: formData.contractTypeId!,
    dateEntry: formData.dateEntry,
    contractStartDate: formData.contractStartDate,
    contractEndDate: formData.contractEndDate ?? null,
    contractModalityId: formData.contractModalityId!,
    companyContractId: formData.companyContractId!,
    positionId: formData.positionId!,
    costCenterId: formData.costCenterId!,
    areaId: formData.areaId!,
    unionCode: formData.unionCode,
  };
}

export function useEmployeeContractEdit(
  employeeId?: number,
  contractId?: number,
): UseEmployeeContractEditResult {
  const navigate = useNavigate();
  const { showNotification, showApiError } = useUI();
  const { t: translate } = useTranslation();
  const [loadingContract, setLoadingContract] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [employeeProfile, setEmployeeProfile] = useState<EmployeeProfile | null>(null);
  const { companies: holdingCompanies, loading: loadingCompanies } = useHoldingCompanies();
  const { statuses: contractStatuses, loading: loadingStatuses } = useContractStatuses();
  const { types: contractTypes, loading: loadingTypes } = useContractTypes();
  const { modalities: contractModalities, loading: loadingModalities } = useContractModalities();
  const { areas, loading: loadingAreas } = useAreas();
  const { costCenters, loading: loadingCostCenters } = useCostCenters();
  const { unions, loading: loadingUnions } = useUnions();

  // React Hook Form instance
  const formMethods = useForm<UpdateEmployeeContractFormData>({
    resolver: zodResolver(updateEmployeeContractSchema),
    mode: 'onBlur',
    defaultValues: {
      contractNumber: 0,
      contractName: '',
      contractStatusId: 1,
      contractTypeId: undefined,
      dateEntry: '',
      contractStartDate: '',
      contractEndDate: null,
      contractModalityId: undefined,
      companyContractId: undefined,
      areaId: undefined,
      positionId: undefined,
      costCenterId: undefined,
      unionCode: '',
    },
  });

  // Watch areaId to reload positions when area changes
  const selectedAreaId = formMethods.watch('areaId');
  const { positions, loading: _loadingPositions } = usePositions(selectedAreaId);

  // Track if initial area was already set (to distinguish initial load from user changes)
  const initialAreaIdSet = useRef(false);
  // Track if user manually changed area (to trigger auto-select when positions load)
  const userChangedArea = useRef(false);

  // Detect when user manually changes areaId (not on initial load)
  useEffect(() => {
    if (!selectedAreaId) {
      return;
    }
    if (!initialAreaIdSet.current) {
      initialAreaIdSet.current = true;
      return;
    }
    userChangedArea.current = true;
     
  }, [selectedAreaId]);

  // Auto-select first position when positions load after user changes area
  useEffect(() => {
    if (userChangedArea.current && positions.length > 0) {
      formMethods.setValue('positionId', positions[0].id, { shouldValidate: false });
      userChangedArea.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions]);

  // Combine all loading states - wait for all catalogs to finish
  const loading = 
    loadingContract || 
    loadingProfile ||
    loadingCompanies || 
    loadingStatuses || 
    loadingTypes || 
    loadingModalities || 
    loadingAreas || 
    loadingCostCenters || 
    loadingUnions;

  // Load employee profile on mount
  useEffect(() => {
    if (!employeeId) {
      setLoadingProfile(false);
      return;
    }

    (async () => {
      setLoadingProfile(true);
      try {
        const response = await employeeGet.getProfile(employeeId);
        if (response.success && response.data) {
          setEmployeeProfile(response.data);
        }
      } catch {
        // Silently fail - profile is optional for contract editing
      } finally {
        setLoadingProfile(false);
      }
    })().catch(() => {
      setLoadingProfile(false);
    });
  }, [employeeId]);

  // Load contract data on mount
  useEffect(() => {
    if (!employeeId || !contractId) {
      setError('Identificador de contrato o empleado inválido');
      setLoadingContract(false);
      return;
    }

    // Track if component is still mounted to prevent state updates after unmount
    let isMounted = true;

    (async () => {
      setLoadingContract(true);
      setError(null);

      try {
        const response = await employeeContractService.get(employeeId, contractId);

        if (!isMounted) return;

        if (!response.success || !response.data) {
          showApiError(response, translate('employee:contracts.messages.notFound'));
          return;
        }

        const contract: LC_GetEmployeeContractByIdOut = response.data;

        // Map transport data to form and use reset() to establish proper isDirty baseline
        const formData = mapEmployeeContractToForm(contract);
        formMethods.reset(formData);
      } catch (catchError) {
        if (!isMounted) return;

        const errorMessage = catchError instanceof Error
          ? catchError.message
          : translate('common:errors.unexpected');
        setError(errorMessage);
        showNotification('error', errorMessage, 5000);
      } finally {
        if (isMounted) {
          setLoadingContract(false);
        }
      }
    })().catch(() => {
      if (isMounted) {
        setError(translate('common:errors.unexpected'));
        setLoadingContract(false);
      }
    });

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [contractId, employeeId, formMethods, showNotification, showApiError, translate]);

  // Navigation target for cancel
  const navigateToList = useCallback(() => {
    const baseUrl = ROUTE_DEFINITIONS.EMPLEADO_CONTRATOS.path;
    const url = employeeId ? `${baseUrl}?employeeId=${employeeId}` : baseUrl;
    void navigate(url);
  }, [navigate, employeeId]);

  // Calculate isDirty state from form
  const isDirty = formMethods.formState.isDirty;

  // Unsaved changes warning hook
  const { handleCancelWithWarning: handleCancel, confirmNavigation } = useUnsavedChangesWarning({
    onConfirmLeave: navigateToList,
    isDirty,
  });

  // Pure business logic handler - testable without form mocking
  const handleSaveContract = useCallback(
    async (contractData: ValidatedUpdateEmployeeContractFormData) => {
      if (!employeeId || !contractId) {
        showNotification('error', 'Identificador de empleado o contrato inválido', 5000);
        return;
      }

      setUpdating(true);

      try {
        const transportData = mapUpdateEmployeeContractFormToTransport(contractData);
        const response = await employeeContractService.update(
          employeeId,
          contractId,
          transportData,
        );

        if (response.success && response.data) {
          showNotification(
            'success',
            translate('employee:contracts.messages.updated'),
            5000,
          );
          // Mark navigation as confirmed to bypass unsaved changes warning
          confirmNavigation();
          const baseUrl = ROUTE_DEFINITIONS.EMPLEADO_CONTRATOS.path;
          const successUrl = employeeId ? `${baseUrl}?employeeId=${employeeId}` : baseUrl;
          void navigate(successUrl);
        } else if (response.error) {
          showApiError(response);

          // Process backend validation errors if available
          if (response.error.validationErrors) {
            injectValidationErrors(response.error.validationErrors, formMethods);
          }
        }
      } catch (catchError) {
        const errorMessage =
          catchError instanceof Error
            ? catchError.message
            : translate('common:errors.unexpected');
        showNotification('error', errorMessage, 5000);
      } finally {
        setUpdating(false);
      }
    },
    [
      employeeId, contractId, showNotification, showApiError,
      translate, navigate, formMethods, confirmNavigation,
    ],
  );
  const handleUpdate = useCallback(async () => {
    const isValid = await formMethods.trigger();
    if (!isValid) {
      showNotification('error', translate('common:validation.formErrors'), 5000);
      return;
    }

    const formData = formMethods.getValues();
    const contractData = buildValidatedContractData(formData, Number(employeeId));

    await handleSaveContract(contractData);
  }, [formMethods, employeeId, handleSaveContract, showNotification, translate]);

  return {
    isLoading: loading,
    updating,
    error,
    employeeProfile,
    handleCancel,
    handleUpdate,
    handleSaveContract,
    contractStatusOptions: contractStatuses.map((status) => ({
      value: String(status.id),
      label: status.name,
    })),
    contractTypeOptions: contractTypes.map((type) => ({
      value: String(type.id),
      label: type.name,
    })),
    contractModalityOptions: contractModalities.map((modality) => ({
      value: String(modality.id),
      label: modality.name,
    })),
    companyOptions: holdingCompanies.map((company) => ({
      value: String(company.id),
      label: company.name,
    })),
    areaOptions: areas.map((area) => ({
      value: String(area.id),
      label: area.name,
    })),
    positionOptions: positions.map((position) => ({
      value: String(position.id),
      label: position.name,
    })),
    costCenterOptions: costCenters.map((costCenter) => ({
      value: String(costCenter.id),
      label: costCenter.name,
    })),
    unionOptions: unions.map((union) => ({
      value: union.id,
      label: union.name,
    })),
    form: {
      register: formMethods.register,
      setValue: formMethods.setValue,
      watch: formMethods.watch,
      errors: formMethods.formState.errors,
      trigger: formMethods.trigger,
      setError: formMethods.setError,
    },
  };
}
